import { p as proxyCustomElement, H, c as createEvent, h } from './index.js';
import { g as getValidityObject, c as checkValidity, r as reportValidity, s as setCustomValidity, a as setInputRef, b as getError, w as watchValue, d as watchError, e as watchWarning, f as renderFormField } from './p-Cg8AdZPK.js';
import { t as toValue, a as toLabel } from './p-CPuegN2k.js';
import { i as isLavOption } from './p-CdnDatdH.js';
import { i as isOtherOption, g as getFilterOptionsFn, h as handleOptions } from './p-D36toVjT.js';
import { H as HiddenHTMLSelect } from './p-BYoISW3Y.js';
import { d as defineCustomElement$k } from './p-D7B-kP0p.js';
import { d as defineCustomElement$j } from './p-BbTg3Car.js';
import { d as defineCustomElement$i } from './p-DzNXepTc.js';
import { d as defineCustomElement$h } from './p-B7ipNcUy.js';
import { d as defineCustomElement$g } from './p-C9_XXP_a.js';
import { d as defineCustomElement$f } from './p-Dpb0Xahb.js';
import { d as defineCustomElement$e } from './p-Da2lAZSv.js';
import { d as defineCustomElement$d } from './p-BY8GADaY.js';
import { d as defineCustomElement$c } from './p-BC_XAddJ.js';
import { d as defineCustomElement$b } from './p-C65gTtHN.js';
import { d as defineCustomElement$a } from './p-DUcqAgyP.js';
import { d as defineCustomElement$9 } from './p-CyejrxOi.js';
import { d as defineCustomElement$8 } from './p-ngrwPeVm.js';
import { d as defineCustomElement$7 } from './p-BU1hvS3W.js';
import { d as defineCustomElement$6 } from './p-DjGiWBv3.js';
import { d as defineCustomElement$5 } from './p-BtEI0Sh2.js';
import { d as defineCustomElement$4 } from './p-CT2oAZeh.js';
import { d as defineCustomElement$3 } from './p-CssqlikZ.js';
import { d as defineCustomElement$2 } from './p-CFAyJ2yW.js';
import { d as defineCustomElement$1 } from './p-ClSUnmWE.js';
import { d as debounce } from './p-Dg7s1ivP.js';

const selectInputCss = ":host{-webkit-font-smoothing:antialiased;-moz-osx-font-smoothing:grayscale;box-sizing:border-box;display:flex;flex-direction:column;font-family:Open Sans;font-size:.875rem;font-weight:400;letter-spacing:-.0004em;line-height:1.25rem;line-height:normal;max-width:100%;min-width:0;width:100%}:host *,:host :after,:host :before{box-sizing:inherit}:host img{flex:none;overflow:hidden;text-overflow:ellipsis;white-space:pre-line}.inner{position:relative}wui-list-item[data-empty]{--wui-list-item--span-font-style:italic}wui-icon[name=chevron-down]{height:1rem;transition:transform .25s ease,opacity .2s ease;width:1rem}wui-input{padding-left:.5rem;padding-right:.5rem;padding-top:.5rem}:host([open]) .box{border:none!important;outline:none!important}";

const emptyValueFallbackStr = 'No value';
const WuiSelectInput = /*@__PURE__*/ proxyCustomElement(class WuiSelectInput extends H {
    constructor() {
        super();
        this.__registerHost();
        this.__attachShadow();
        this.validityChecked = createEvent(this, "validityChecked", 7);
        this.validityReported = createEvent(this, "validityReported", 7);
        this.required = false;
        this.validity = getValidityObject();
        this.validationMessage = '';
        this.customValidation = undefined;
        this.checkValidity = checkValidity(this);
        this.reportValidity = reportValidity(this);
        this.setCustomValidity = setCustomValidity(this);
        this.showValidity = false;
        this.setInputRef = setInputRef(this);
        this.dirty = false;
        this.getError = getError(this);
        /**
         * Placeholder (displayed when value is undefined)
         */
        this.placeholder = '';
        /**
         * Disable component
         */
        this.disabled = false;
        /**
         * Show loading spinner
         *
         * Note: the loading state is automatically enabled when using async options
         */
        this.loading = false;
        /**
         * If results can be filtered through search input
         */
        this.searchable = false;
        this.open = false;
        this.searchQuery = '';
        this._optionsResolved = undefined;
        this.abortControllers = [];
        this.blindSearchQuery = undefined;
        this.triggerBlindSearch = debounce((query) => {
            if (!(this.optionsResolved?.length && this.fuseSearch)) {
                return;
            }
            const wuiListElement = this.findWuiListElement();
            if (!wuiListElement) {
                return;
            }
            const results = this.fuseSearch(query);
            const result = results.length ? results[0] : undefined;
            if (isOtherOption(result)) {
                return;
            }
            // Find the option that matches the value from our Fuse result
            const foundOption = this.optionsResolved.find((o) => {
                if (isOtherOption(o)) {
                    return;
                }
                return toValue(o) === toValue(result);
            });
            if (!foundOption || isOtherOption(foundOption)) {
                return;
            }
            if (this.open) {
                // Activate/highlight found item
                wuiListElement.activeItemIndex = this.optionsResolved.indexOf(foundOption);
            }
            else {
                // Select option immediately when closed
                this.selectOption(foundOption);
            }
        }, 50);
        this.resolveInitialOption = (() => {
            let ran = false;
            return async () => {
                // Only resolve once
                if (ran) {
                    return;
                }
                // Don't try to resolve undefined value
                if (typeof this.value === 'undefined' || !this.resolveInitialValue) {
                    return;
                }
                ran = true;
                // Resolve initial value
                this.selectedOption = await this.resolveInitialValue.apply(this);
            };
        })();
        this.filterOptions = getFilterOptionsFn(this);
        this.filterOptionsDebounced = debounce(this.filterOptions, 250);
        this.handleSearchInput = async (e) => {
            const target = e.currentTarget;
            if (!target) {
                return;
            }
            this.searchQuery = target.value ?? '';
            // Set filtered options from query, either through a dynamic options
            // function or through client-side filtering with Fuse
            this.filterOptionsDebounced();
        };
    }
    _handleValueChange(newValue) {
        watchValue(this, newValue);
    }
    _watchError(...args) {
        watchError(this, ...args);
    }
    _watchWarning(...args) {
        watchWarning(this, ...args);
    }
    //////// FORM FIELD END ////////
    watchValue(newValue, prevValue) {
        this.selectedOption = this.findSelectedOption(newValue);
        // If value is being initialized after initial mount
        if (typeof prevValue === 'undefined' && newValue && !this.selectedOption) {
            this.resolveInitialOption();
        }
    }
    async watchOptions(newValue) {
        await handleOptions(this, newValue);
        this.assertSelectedOption();
    }
    get optionsResolved() {
        return this._optionsResolved;
    }
    set optionsResolved(value) {
        this._optionsResolved = value;
        // Reset active list item when options change
        const wuiListElement = this.findWuiListElement();
        if (wuiListElement) {
            wuiListElement.activeItemIndex = -1;
        }
        this.checkValidity();
    }
    async _handleKeyDown(e) {
        // ⚠️ Use preventDefault when key event has been successfully handled
        if (this.handleBlindSearch(e)) {
            e.preventDefault();
            e.stopPropagation();
            return;
        }
        const wuiMenuPaneElement = this.elm.shadowRoot?.querySelector('wui-menu-pane');
        if (wuiMenuPaneElement && (await wuiMenuPaneElement.handleKeyDown(e))) {
            // Event was handled by menu pane
            // sync open state
            this.open = !!wuiMenuPaneElement.open;
            return;
        }
        const wuiListElement = this.findWuiListElement();
        if (wuiListElement && (await wuiListElement.handleKeyDown(e))) {
            return;
        }
    }
    handleBlindSearch(e) {
        if (this.searchable) {
            // Don't do any blind searching if we have an explicit search field
            return false;
        }
        // Only handle keys or digits. Spaces only when there is a query.
        const isKeyOrDigit = e.code.startsWith('Key') || e.code.startsWith('Digit');
        const isSpace = e.key === ' ';
        if (!(isKeyOrDigit || (isSpace && this.blindSearchQuery))) {
            return false;
        }
        // Add key to blind query
        this.blindSearchQuery = (typeof this.blindSearchQuery === 'undefined' ? '' : this.blindSearchQuery) + e.key;
        // Clear search query after timeout
        this.blindSearchQueryTimeout && clearTimeout(this.blindSearchQueryTimeout);
        this.blindSearchQueryTimeout = setTimeout(() => {
            this.blindSearchQuery = '';
        }, 1000);
        this.triggerBlindSearch(this.blindSearchQuery);
        // Return true to let the caller know we've handled this event successfully
        return true;
    }
    findWuiListElement() {
        return this.elm.shadowRoot?.querySelector('wui-list');
    }
    toggleMenuPane(open = !this.open) {
        if (this.open === open) {
            return;
        }
        this.open = open;
        this.blindSearchQuery = '';
        if (this.searchable && this.searchInputElm && open) {
            requestAnimationFrame(() => {
                this.searchInputElm?.focus();
            });
        }
        if (open) {
            const wuiListElement = this.findWuiListElement();
            if (wuiListElement) {
                // Highlight current value's item when opening
                const activeIdx = this.optionsResolved?.findIndex((v) => !isOtherOption(v) && toValue(v) === this.value);
                if (activeIdx !== -1) {
                    wuiListElement.activeItemIndex = activeIdx;
                }
            }
        }
    }
    setValue(newValue) {
        if (this.value === newValue) {
            return;
        }
        if (!this.inputElm) {
            return;
        }
        // Manually update inner input element
        this.inputElm.value = newValue ?? '';
        this.value = newValue;
        this.dirty = true;
        this.checkValidity();
        // Dispatch event because the hidden inner select element won't do it
        this.elm.dispatchEvent(new window.Event('input', { bubbles: true }));
    }
    selectOption(option) {
        if (typeof option === 'undefined' || !this.inputElm) {
            return;
        }
        // Cache the option itself, alongside the value.
        this.selectedOption = option;
        const newValue = toValue(option);
        this.setValue(newValue);
        // Close menu
        this.toggleMenuPane(false);
        // Return focus to button
        this.elm.querySelector('button')?.focus();
    }
    componentWillLoad() {
        (async () => {
            const old = {
                loading: this.loading,
                disabled: this.disabled,
            };
            this.loading = true;
            this.disabled = true;
            try {
                await Promise.all([
                    // Trigger options validation for initial value
                    this.watchOptions(this.options),
                    this.resolveInitialOption(),
                ]);
            }
            catch {
                // ignore
            }
            finally {
                this.loading = old.loading;
                this.disabled = old.disabled;
                this.assertSelectedOption();
                this.checkValidity();
            }
        })();
    }
    findSelectedOption(value) {
        return this.optionsResolved?.find((o) => {
            return isLavOption(o) && toValue(o) === toValue(value);
        });
    }
    assertSelectedOption() {
        if (this.selectedOption) {
            return;
        }
        // After we (should) have a list of options, find the selected option from the current value
        this.selectedOption = this.findSelectedOption(this.value);
    }
    getErrorType() {
        // This state should always produce a warning. It means we can not resolve the selected option from the value.
        if (typeof this.selectedOption === 'undefined' && this.value && !this.loading) {
            return 'warning';
        }
        return this.getError().type;
    }
    renderOption(option, idx, slot) {
        if (isOtherOption(option)) {
            switch (option.type) {
                case 'header':
                    return h("wui-list-item-header", { slot: slot, key: `${idx}_${slot ?? 'body'}_header`, ...option });
                case 'divider':
                    return h("wui-list-item-divider", { slot: slot, key: `${idx}_${slot ?? 'body'}_divider` });
                case 'meta':
                    return h("wui-list-item-meta", { slot: slot, key: `${idx}_${slot ?? 'body'}_meta`, ...option });
            }
        }
        const value = toValue(option);
        const label = toLabel(option);
        return (h("wui-list-item", { slot: slot, key: `${idx}_${slot ?? 'body'}_${value}`, disabled: !this.open || (typeof option === 'object' ? option.disabled : false), onClick: () => this.selectOption(option), description: typeof option === 'object' && 'description' in option ? option.description : undefined, label: value === '' ? label || emptyValueFallbackStr : label, "data-empty": value === '' }));
    }
    renderInner() {
        let displayLabel;
        if (typeof this.selectedOption === 'undefined') {
            if (this.value && !this.loading) {
                // Fall back to displaying a value
                displayLabel = this.value;
            }
            else {
                // This is perfectly fine, display the placeholder
                displayLabel = this.placeholder;
            }
        }
        else {
            const optionLabel = isLavOption(this.selectedOption) ? toLabel(this.selectedOption) : '';
            const optionValue = isLavOption(this.selectedOption) ? toValue(this.selectedOption) : undefined;
            displayLabel = optionValue === '' ? h("i", null, optionLabel || emptyValueFallbackStr) : optionLabel;
        }
        const noSearchResults = this.searchable && this.searchQuery.trim() ? (this.optionsResolved ?? []).length === 0 : undefined;
        const noResultsLabel = this.searchable ? 'No results match your search' : 'No results';
        const extraOptions = this.extraOptions?.apply(this, [{ noSearchResults }]);
        return (h("div", { class: "inner" }, h(HiddenHTMLSelect, { ref: this.setInputRef, required: !!this.required, name: this.name, options: this.optionsResolved, disabled: this.disabled, value: this.value, multiple: false }), h("wui-input-menu", { open: this.open, onOpenChanged: (e) => {
                this.toggleMenuPane(e.detail);
            }, menuPane: this.menuPane, disabled: this.disabled, loading: this.loading, inputButton: {
                type: this.inputButtonOptions?.type || 'plain',
                label: this.inputButtonOptions?.label || displayLabel,
                size: this.inputButtonOptions?.size,
                value: this.value,
                errorType: this.getErrorType(),
                addon: this.addon,
                readOnly: this.canClear !== true,
            }, onClear: () => {
                if (!this.canClear) {
                    return;
                }
                this.setValue(undefined);
            } }, this.searchable && (h("wui-input", { placeholder: "Type to search", type: "search", value: this.searchQuery, onInput: this.handleSearchInput, loading: this.loading, ref: (elm) => {
                if (!elm) {
                    return;
                }
                this.searchInputElm = elm;
            } })), h("wui-list", { scrollSize: "md" }, extraOptions?.header?.map((item, idx) => {
            return this.renderOption(item, idx, 'header');
        }), noSearchResults ? (h("wui-list-item-meta", { label: noResultsLabel, loading: this.loading })) : (this.optionsResolved?.map((option, idx) => {
            return this.renderOption(option, idx, undefined);
        })), extraOptions?.footer?.map((item, idx) => {
            return this.renderOption(item, idx, 'footer');
        })))));
    }
    render() {
        return renderFormField(this, this.renderInner(), {
            host: {
                role: 'listbox',
                open: this.open,
                // Overrides the error-type property from `getAttributes`
                'error-type': this.getErrorType(),
            },
        });
    }
    get elm() { return this; }
    static get watchers() { return {
        "value": ["_handleValueChange", "watchValue"],
        "error": ["_watchError"],
        "warning": ["_watchWarning"],
        "options": ["watchOptions"]
    }; }
    static get style() { return selectInputCss; }
}, [257, "wui-select-input", {
        "value": [1025],
        "required": [8],
        "error": [8],
        "warning": [8],
        "validate": [16],
        "validity": [1040],
        "validationMessage": [1025, "validation-message"],
        "checkValidity": [16, "check-validity"],
        "reportValidity": [16, "report-validity"],
        "setCustomValidity": [16, "set-custom-validity"],
        "formField": [16, "form-field"],
        "inputButtonOptions": [16, "input-button-options"],
        "width": [513],
        "name": [1],
        "options": [16],
        "resolveInitialValue": [16, "resolve-initial-value"],
        "placeholder": [1],
        "addon": [1],
        "menuPane": [16, "menu-pane"],
        "disabled": [1540],
        "loading": [1540],
        "searchable": [4],
        "canClear": [4, "can-clear"],
        "extraOptions": [16, "extra-options"],
        "customValidation": [32],
        "showValidity": [32],
        "selectedOption": [32],
        "open": [32],
        "searchQuery": [32],
        "searchInputElm": [32],
        "_optionsResolved": [32]
    }, [[0, "keydown", "_handleKeyDown"]], {
        "value": ["_handleValueChange", "watchValue"],
        "error": ["_watchError"],
        "warning": ["_watchWarning"],
        "options": ["watchOptions"]
    }]);
function defineCustomElement() {
    if (typeof customElements === "undefined") {
        return;
    }
    const components = ["wui-select-input", "wui-badge", "wui-box", "wui-check-input", "wui-close-button", "wui-divider", "wui-form-field", "wui-icon", "wui-input", "wui-input-button", "wui-input-menu", "wui-list", "wui-list-item", "wui-list-item-divider", "wui-list-item-header", "wui-list-item-meta", "wui-loader", "wui-menu-pane", "wui-status", "wui-tooltip", "wui-tooltip-label"];
    components.forEach(tagName => { switch (tagName) {
        case "wui-select-input":
            if (!customElements.get(tagName)) {
                customElements.define(tagName, WuiSelectInput);
            }
            break;
        case "wui-badge":
            if (!customElements.get(tagName)) {
                defineCustomElement$k();
            }
            break;
        case "wui-box":
            if (!customElements.get(tagName)) {
                defineCustomElement$j();
            }
            break;
        case "wui-check-input":
            if (!customElements.get(tagName)) {
                defineCustomElement$i();
            }
            break;
        case "wui-close-button":
            if (!customElements.get(tagName)) {
                defineCustomElement$h();
            }
            break;
        case "wui-divider":
            if (!customElements.get(tagName)) {
                defineCustomElement$g();
            }
            break;
        case "wui-form-field":
            if (!customElements.get(tagName)) {
                defineCustomElement$f();
            }
            break;
        case "wui-icon":
            if (!customElements.get(tagName)) {
                defineCustomElement$e();
            }
            break;
        case "wui-input":
            if (!customElements.get(tagName)) {
                defineCustomElement$d();
            }
            break;
        case "wui-input-button":
            if (!customElements.get(tagName)) {
                defineCustomElement$c();
            }
            break;
        case "wui-input-menu":
            if (!customElements.get(tagName)) {
                defineCustomElement$b();
            }
            break;
        case "wui-list":
            if (!customElements.get(tagName)) {
                defineCustomElement$a();
            }
            break;
        case "wui-list-item":
            if (!customElements.get(tagName)) {
                defineCustomElement$9();
            }
            break;
        case "wui-list-item-divider":
            if (!customElements.get(tagName)) {
                defineCustomElement$8();
            }
            break;
        case "wui-list-item-header":
            if (!customElements.get(tagName)) {
                defineCustomElement$7();
            }
            break;
        case "wui-list-item-meta":
            if (!customElements.get(tagName)) {
                defineCustomElement$6();
            }
            break;
        case "wui-loader":
            if (!customElements.get(tagName)) {
                defineCustomElement$5();
            }
            break;
        case "wui-menu-pane":
            if (!customElements.get(tagName)) {
                defineCustomElement$4();
            }
            break;
        case "wui-status":
            if (!customElements.get(tagName)) {
                defineCustomElement$3();
            }
            break;
        case "wui-tooltip":
            if (!customElements.get(tagName)) {
                defineCustomElement$2();
            }
            break;
        case "wui-tooltip-label":
            if (!customElements.get(tagName)) {
                defineCustomElement$1();
            }
            break;
    } });
}
defineCustomElement();

export { WuiSelectInput as W, defineCustomElement as d };
